import { useState, useEffect } from 'react';
import { getEvents, getEvent, addLabel, type EventRecord, type EventsQuery, type Label, type SmartSignals, type SuspectContributor } from '../api/client';
import { format } from 'date-fns';

function VerdictBadge({ verdict }: { verdict: string }) {
  const classes: Record<string, string> = {
    ALLOW: 'badge badge-allow',
    BLOCK: 'badge badge-block',
    CHALLENGE: 'badge badge-challenge',
    THROTTLE: 'badge badge-throttle',
    DELAY: 'badge',
  };
  return <span className={classes[verdict] || 'badge'}>{verdict}</span>;
}

function OutcomeBadge({ code }: { code?: string }) {
  if (!code) return <span className="text-gray-400">-</span>;
  
  const baseClasses = 'text-xs px-2 py-1 rounded font-medium';
  
  if (code.startsWith('ALLOW_')) {
    return <span className={`${baseClasses} bg-green-500/20 text-green-400`}>{code}</span>;
  }
  if (code.startsWith('BLOCK_')) {
    return <span className={`${baseClasses} bg-red-500/20 text-red-400`}>{code}</span>;
  }
  if (code.startsWith('CHALLENGE_')) {
    return <span className={`${baseClasses} bg-yellow-500/20 text-yellow-400`}>{code}</span>;
  }
  if (code.startsWith('THROTTLE_')) {
    return <span className={`${baseClasses} bg-orange-500/20 text-orange-400`}>{code}</span>;
  }
  return <span className={`${baseClasses} bg-gray-500/20 text-gray-400`}>{code}</span>;
}

function TelemetryStatusBadge({ status }: { status?: string }) {
  if (!status) return <span className="text-gray-400">-</span>;
  
  const classes: Record<string, string> = {
    VALID: 'bg-green-500/20 text-green-400',
    MISSING_HEADER: 'bg-gray-500/20 text-gray-400',
    MISSING_FIELDS: 'bg-yellow-500/20 text-yellow-400',
    INVALID_SIGNATURE: 'bg-red-500/20 text-red-400',
    TOKEN_EXPIRED: 'bg-orange-500/20 text-orange-400',
    TOKEN_INVALID: 'bg-red-500/20 text-red-400',
    TIMESTAMP_INVALID: 'bg-yellow-500/20 text-yellow-400',
    PARSE_ERROR: 'bg-red-500/20 text-red-400',
  };
  
  return (
    <span className={`text-xs px-2 py-1 rounded font-medium ${classes[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  );
}

function ScoreBadge({ score }: { score: number }) {
  let colorClass = 'text-green-400';
  if (score >= 70) colorClass = 'text-red-400';
  else if (score >= 30) colorClass = 'text-yellow-400';
  return <span className={`font-bold ${colorClass}`}>{score}</span>;
}

// Fingerprint Suspect Score Badge
function SuspectScoreBadge({ score }: { score?: number }) {
  if (score === undefined) return <span className="text-gray-400">-</span>;
  
  let colorClass = 'text-green-400';
  let bgClass = 'bg-green-500/10';
  if (score >= 70) { colorClass = 'text-red-400'; bgClass = 'bg-red-500/10'; }
  else if (score >= 50) { colorClass = 'text-orange-400'; bgClass = 'bg-orange-500/10'; }
  else if (score >= 30) { colorClass = 'text-yellow-400'; bgClass = 'bg-yellow-500/10'; }
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${bgClass}`}>
      <span className={`text-xl font-bold ${colorClass}`}>{score}</span>
      <span className="text-xs text-gray-400">/ 100</span>
    </div>
  );
}

// Signal Badge Component
function SignalBadge({ 
  label, 
  value, 
  confidence 
}: { 
  label: string; 
  value: boolean | string; 
  confidence?: string;
}) {
  let bgClass = 'bg-gray-700';
  let textClass = 'text-gray-300';
  
  if (typeof value === 'boolean') {
    if (value) {
      bgClass = confidence === 'high' ? 'bg-red-500/20' : 
                confidence === 'medium' ? 'bg-orange-500/20' : 'bg-yellow-500/20';
      textClass = confidence === 'high' ? 'text-red-400' : 
                  confidence === 'medium' ? 'text-orange-400' : 'text-yellow-400';
    } else {
      bgClass = 'bg-green-500/10';
      textClass = 'text-green-400';
    }
  } else if (typeof value === 'string') {
    if (value === 'bad') {
      bgClass = 'bg-red-500/20';
      textClass = 'text-red-400';
    } else if (value === 'good' || value === 'notDetected') {
      bgClass = 'bg-green-500/10';
      textClass = 'text-green-400';
    }
  }
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs ${bgClass} ${textClass}`}>
      <span className="font-medium">{label}</span>
      <span>{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
      {confidence && <span className="text-gray-400">({confidence})</span>}
    </span>
  );
}

// Contributor Bar
function ContributorBar({ contributor }: { contributor: SuspectContributor }) {
  const width = Math.min(Math.abs(contributor.weight) * 5, 100);
  const isPositive = contributor.weight > 0;
  
  return (
    <div className="flex items-center gap-3 text-sm py-1">
      <div className="w-32 text-gray-300">{contributor.signal}</div>
      <div className="flex-1 h-4 bg-gray-700 rounded overflow-hidden">
        <div 
          className={`h-full ${isPositive ? 'bg-red-500' : 'bg-green-500'}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className={`w-12 text-right font-mono ${isPositive ? 'text-red-400' : 'text-green-400'}`}>
        {contributor.weight > 0 ? '+' : ''}{contributor.weight}
      </div>
      <div className="w-20 text-gray-400 text-xs">
        {String(contributor.value)}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<EventRecord | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'suspect' | 'signals'>('overview');

  // Filters
  const [filters, setFilters] = useState<EventsQuery>({
    page: 1,
    limit: 50,
  });

  useEffect(() => {
    loadEvents();
  }, [filters]);

  async function loadEvents() {
    setLoading(true);
    setError(null);

    const result = await getEvents(filters);

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setEvents(result.data.events);
      setTotal(result.data.total);
    }

    setLoading(false);
  }

  async function handleViewEvent(event: EventRecord) {
    const result = await getEvent(event.decision_id);
    if (result.data) {
      setSelectedEvent(result.data.event);
      setSelectedLabels(result.data.labels);
      setShowModal(true);
    }
  }

  async function handleAddLabel(label: 'GOOD' | 'BAD' | 'SUSPICIOUS' | 'FALSE_POSITIVE') {
    if (!selectedEvent) return;

    const result = await addLabel({
      decision_id: selectedEvent.decision_id,
      label,
      source: 'MANUAL',
    });

    if (result.data) {
      // Reload event to get updated labels
      const updated = await getEvent(selectedEvent.decision_id);
      if (updated.data) {
        setSelectedLabels(updated.data.labels);
      }
    }
  }

  function handleFilterChange(key: keyof EventsQuery, value: string | number | undefined) {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: 1, // Reset to first page on filter change
    }));
  }

  const pageCount = Math.ceil(total / (filters.limit || 50));

  // Parse JSON fields for display
  function parseJsonField<T>(jsonString?: string): T | null {
    if (!jsonString) return null;
    try {
      return JSON.parse(jsonString) as T;
    } catch {
      return null;
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Events</h1>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Route Class</label>
            <select
              className="select"
              value={filters.route_class || ''}
              onChange={(e) => handleFilterChange('route_class', e.target.value)}
            >
              <option value="">All</option>
              <option value="SIGNUP">Signup</option>
              <option value="LOGIN">Login</option>
              <option value="FORGOT_PASSWORD">Forgot Password</option>
              <option value="OTP_START">OTP Start</option>
              <option value="OTP_VERIFY">OTP Verify</option>
              <option value="PAYMENT">Payment</option>
              <option value="PROMO">Promo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Verdict</label>
            <select
              className="select"
              value={filters.verdict || ''}
              onChange={(e) => handleFilterChange('verdict', e.target.value)}
            >
              <option value="">All</option>
              <option value="ALLOW">Allow</option>
              <option value="CHALLENGE">Challenge</option>
              <option value="THROTTLE">Throttle</option>
              <option value="BLOCK">Block</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Outcome Code</label>
            <select
              className="select"
              value={filters.outcome_code || ''}
              onChange={(e) => handleFilterChange('outcome_code', e.target.value)}
            >
              <option value="">All</option>
              <option value="ALLOW_TRUSTED_DEVICE">Allow - Trusted Device</option>
              <option value="ALLOW_LOW_RISK">Allow - Low Risk</option>
              <option value="ALLOW_ALLOWLISTED">Allow - Allowlisted</option>
              <option value="CHALLENGE_TURNSTILE">Challenge - Turnstile</option>
              <option value="BLOCK_BOT_AUTOMATION">Block - Bot</option>
              <option value="BLOCK_RATE_LIMIT">Block - Rate Limit</option>
              <option value="BLOCK_DENYLISTED">Block - Denylisted</option>
              <option value="THROTTLE_SOFT">Throttle - Soft</option>
              <option value="THROTTLE_HARD">Throttle - Hard</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Telemetry Status</label>
            <select
              className="select"
              value={filters.telemetry_status || ''}
              onChange={(e) => handleFilterChange('telemetry_status', e.target.value)}
            >
              <option value="">All</option>
              <option value="VALID">Valid</option>
              <option value="MISSING_HEADER">Missing</option>
              <option value="INVALID_SIGNATURE">Invalid Signature</option>
              <option value="TOKEN_EXPIRED">Token Expired</option>
              <option value="TIMESTAMP_INVALID">Timestamp Invalid</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Min Score</label>
            <input
              type="number"
              className="input"
              placeholder="0"
              min="0"
              max="100"
              value={filters.min_score || ''}
              onChange={(e) => handleFilterChange('min_score', e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max Score</label>
            <input
              type="number"
              className="input"
              placeholder="100"
              min="0"
              max="100"
              value={filters.max_score || ''}
              onChange={(e) => handleFilterChange('max_score', e.target.value ? parseInt(e.target.value) : undefined)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Country</label>
            <input
              type="text"
              className="input"
              placeholder="US"
              value={filters.country || ''}
              onChange={(e) => handleFilterChange('country', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Email Domain</label>
            <input
              type="text"
              className="input"
              placeholder="example.com"
              value={filters.email_domain || ''}
              onChange={(e) => handleFilterChange('email_domain', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">IP Hash</label>
            <input
              type="text"
              className="input"
              placeholder="abc123..."
              value={filters.ip_hash || ''}
              onChange={(e) => handleFilterChange('ip_hash', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Device Hash</label>
            <input
              type="text"
              className="input"
              placeholder="abc123..."
              value={filters.device_id_hash || ''}
              onChange={(e) => handleFilterChange('device_id_hash', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Email Hash</label>
            <input
              type="text"
              className="input"
              placeholder="abc123..."
              value={filters.email_hash || ''}
              onChange={(e) => handleFilterChange('email_hash', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Phone Hash</label>
            <input
              type="text"
              className="input"
              placeholder="abc123..."
              value={filters.phone_hash || ''}
              onChange={(e) => handleFilterChange('phone_hash', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg">
          Error loading events: {error}
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-400 mb-2">
            Showing {events.length} of {total} events
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Route</th>
                  <th>Verdict</th>
                  <th>Outcome</th>
                  <th>Score</th>
                  <th>Telemetry</th>
                  <th>Country</th>
                  <th>IP Hash</th>
                  <th>Latency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.decision_id}>
                    <td className="whitespace-nowrap">
                      {format(new Date(event.created_at), 'MMM d, HH:mm:ss')}
                    </td>
                    <td>
                      <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                        {event.route_class}
                      </span>
                    </td>
                    <td>
                      <VerdictBadge verdict={event.verdict} />
                    </td>
                    <td>
                      <OutcomeBadge code={event.outcome_code} />
                    </td>
                    <td>
                      <ScoreBadge score={event.risk_score} />
                    </td>
                    <td>
                      <TelemetryStatusBadge status={event.telemetry_validation_status} />
                    </td>
                    <td>{event.country || '-'}</td>
                    <td className="font-mono text-xs">{event.ip_hash?.substring(0, 8)}...</td>
                    <td>{event.latency_ms}ms</td>
                    <td>
                      <button
                        onClick={() => handleViewEvent(event)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-400">
              Page {filters.page} of {pageCount}
            </div>
            <div className="flex gap-2">
              <button
                className="btn btn-secondary"
                disabled={filters.page === 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary"
                disabled={filters.page === pageCount}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Event detail modal */}
      {showModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold">Event Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Tab Navigation */}
              <div className="flex gap-1 mb-6 bg-gray-900 p-1 rounded-lg">
                {[
                  { id: 'overview', label: 'Overview', icon: '📋' },
                  { id: 'suspect', label: 'Suspect Score', icon: '🎯' },
                  { id: 'signals', label: 'Smart Signals', icon: '🔍' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    <span>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="card">
                      <div className="text-sm text-gray-400">Verdict</div>
                      <VerdictBadge verdict={selectedEvent.verdict} />
                    </div>
                    <div className="card">
                      <div className="text-sm text-gray-400">Outcome Code</div>
                      <OutcomeBadge code={selectedEvent.outcome_code} />
                    </div>
                    <div className="card">
                      <div className="text-sm text-gray-400">Risk Score</div>
                      <ScoreBadge score={selectedEvent.risk_score} />
                    </div>
                    <div className="card">
                      <div className="text-sm text-gray-400">Suspect Score</div>
                      <SuspectScoreBadge score={selectedEvent.suspect_score} />
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="card">
                    <h3 className="font-semibold mb-3">Request Details</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-400">Decision ID</div>
                      <div className="font-mono">{selectedEvent.decision_id}</div>
                      <div className="text-gray-400">FP Visitor ID</div>
                      <div className="font-mono text-xs">{selectedEvent.fp_visitor_id || '-'}</div>
                      <div className="text-gray-400">FP Request ID</div>
                      <div className="font-mono text-xs">{selectedEvent.fp_request_id || '-'}</div>
                      <div className="text-gray-400">Time</div>
                      <div>{format(new Date(selectedEvent.created_at), 'PPpp')}</div>
                      <div className="text-gray-400">Route</div>
                      <div>{selectedEvent.route}</div>
                      <div className="text-gray-400">Latency</div>
                      <div>{selectedEvent.latency_ms}ms</div>
                    </div>
                  </div>

                  {/* Network & Identifiers */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="card">
                      <h3 className="font-semibold mb-2">Network</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-gray-400">IP Hash</div>
                        <div className="font-mono">{selectedEvent.ip_hash?.substring(0, 16)}...</div>
                        <div className="text-gray-400">Country</div>
                        <div>{selectedEvent.country || '-'}</div>
                        <div className="text-gray-400">ASN</div>
                        <div>{selectedEvent.asn || '-'}</div>
                      </div>
                    </div>
                    <div className="card">
                      <h3 className="font-semibold mb-2">Identifiers</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-gray-400">Device Hash</div>
                        <div className="font-mono text-xs">{selectedEvent.device_id_hash?.substring(0, 16) || '-'}</div>
                        <div className="text-gray-400">Email Domain</div>
                        <div>{selectedEvent.email_domain || '-'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Reason Codes */}
                  <div className="card">
                    <h3 className="font-semibold mb-2">Reason Codes</h3>
                    <div className="flex flex-wrap gap-2">
                      {parseJsonField<string[]>(selectedEvent.reasons)?.map((reason: string) => (
                        <span key={reason} className="bg-gray-700 px-2 py-1 rounded text-sm">
                          {reason}
                        </span>
                      )) || <span className="text-gray-400">No reasons recorded</span>}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'suspect' && (
                <div className="space-y-4">
                  {/* Suspect Score Header */}
                  <div className="card bg-gradient-to-r from-gray-800 to-gray-900">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold mb-1">Fingerprint Suspect Score</h3>
                        <p className="text-sm text-gray-400">
                          Official Fingerprint score - version {selectedEvent.suspect_score_version || 'default'}
                        </p>
                      </div>
                      <SuspectScoreBadge score={selectedEvent.suspect_score} />
                    </div>
                  </div>

                  {/* Contributors */}
                  {selectedEvent.suspect_contributors && (
                    <div className="card">
                      <h3 className="font-semibold mb-3">Score Contributors</h3>
                      <div className="space-y-1">
                        {parseJsonField<SuspectContributor[]>(selectedEvent.suspect_contributors)?.map((contributor, idx) => (
                          <ContributorBar key={idx} contributor={contributor} />
                        )) || <span className="text-gray-400">No contributor data</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-3">
                        These weights are for explainability only - the score is computed by Fingerprint
                      </p>
                    </div>
                  )}

                  {/* Score Comparison */}
                  <div className="card">
                    <h3 className="font-semibold mb-3">Score Comparison</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-900 p-4 rounded-lg text-center">
                        <div className="text-sm text-gray-400 mb-1">Internal Risk Score</div>
                        <div className="text-3xl font-bold text-blue-400">{selectedEvent.risk_score}</div>
                      </div>
                      <div className="bg-gray-900 p-4 rounded-lg text-center">
                        <div className="text-sm text-gray-400 mb-1">Fingerprint Suspect Score</div>
                        <div className={`text-3xl font-bold ${
                          (selectedEvent.suspect_score || 0) >= 50 ? 'text-red-400' : 
                          (selectedEvent.suspect_score || 0) >= 30 ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {selectedEvent.suspect_score || '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Matched Rules */}
                  {selectedEvent.matched_rules && (
                    <div className="card">
                      <h3 className="font-semibold mb-3">Rules That Evaluated</h3>
                      <div className="space-y-2">
                        {parseJsonField<Array<{
                          rule_id: string;
                          rule_name: string;
                          category: string;
                          severity: string;
                          contribution: number;
                        }>>(selectedEvent.matched_rules)?.map((rule, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-900 p-3 rounded">
                            <div>
                              <div className="font-medium">{rule.rule_name}</div>
                              <div className="text-xs text-gray-400">{rule.category}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                rule.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                                rule.severity === 'HIGH' ? 'bg-orange-500/20 text-orange-400' :
                                rule.severity === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>
                                {rule.severity}
                              </span>
                            </div>
                          </div>
                        )) || <span className="text-gray-400">No rules matched</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'signals' && (
                <div className="space-y-4">
                  {/* Signal Summary */}
                  <div className="card">
                    <h3 className="font-semibold mb-3">Signal Overview</h3>
                    <div className="flex flex-wrap gap-2">
                      <SignalBadge label="Bot" value={selectedEvent.bot_result || 'notDetected'} />
                      <SignalBadge label="VPN" value={!!selectedEvent.vpn_result} confidence={selectedEvent.vpn_confidence} />
                      <SignalBadge label="Proxy" value={!!selectedEvent.proxy_result} />
                      <SignalBadge label="Tampering" value={!!selectedEvent.tampering_result} />
                      <SignalBadge label="VM" value={!!selectedEvent.virtual_machine} />
                      <SignalBadge label="DevTools" value={!!selectedEvent.developer_tools} />
                      <SignalBadge label="Datacenter" value={!!selectedEvent.datacenter_result} />
                      <SignalBadge label="High Activity" value={!!selectedEvent.high_activity_device} />
                    </div>
                  </div>

                  {/* Detailed Signals */}
                  {selectedEvent.smart_signals && (
                    <div className="space-y-4">
                      {(() => {
                        const signals = parseJsonField<SmartSignals>(selectedEvent.smart_signals);
                        if (!signals) return <div className="text-gray-400">No signal data available</div>;
                        
                        return (
                          <>
                            {/* Bot Detection */}
                            {signals.bot && (
                              <div className="card">
                                <h4 className="font-medium mb-2 text-yellow-400">🤖 Bot Detection</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="text-gray-400">Result</div>
                                  <div className={signals.bot.result === 'bad' ? 'text-red-400' : 'text-green-400'}>
                                    {signals.bot.result}
                                  </div>
                                  {signals.bot.type && (
                                    <>
                                      <div className="text-gray-400">Type</div>
                                      <div>{signals.bot.type}</div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* VPN Detection */}
                            {signals.vpn && (
                              <div className="card">
                                <h4 className="font-medium mb-2 text-orange-400">🔒 VPN Detection</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="text-gray-400">Detected</div>
                                  <div className={signals.vpn.result ? 'text-red-400' : 'text-green-400'}>
                                    {signals.vpn.result ? 'Yes' : 'No'}
                                  </div>
                                  <div className="text-gray-400">Confidence</div>
                                  <div className="capitalize">{signals.vpn.confidence}</div>
                                  {signals.vpn.originTimezone && (
                                    <>
                                      <div className="text-gray-400">Origin Timezone</div>
                                      <div>{signals.vpn.originTimezone}</div>
                                    </>
                                  )}
                                  {signals.vpn.methods && (
                                    <>
                                      <div className="text-gray-400">Methods</div>
                                      <div className="flex flex-wrap gap-1">
                                        {Object.entries(signals.vpn.methods)
                                          .filter(([, v]) => v)
                                          .map(([k]) => (
                                            <span key={k} className="bg-gray-700 px-2 py-0.5 rounded text-xs">
                                              {k.replace(/([A-Z])/g, ' $1').trim()}
                                            </span>
                                          ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Tampering */}
                            {signals.tampering && (
                              <div className="card">
                                <h4 className="font-medium mb-2 text-red-400">⚠️ Tampering Detection</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="text-gray-400">Detected</div>
                                  <div className={signals.tampering.result ? 'text-red-400' : 'text-green-400'}>
                                    {signals.tampering.result ? 'Yes' : 'No'}
                                  </div>
                                  {signals.tampering.details?.anomalyScore !== undefined && (
                                    <>
                                      <div className="text-gray-400">Anomaly Score</div>
                                      <div className={signals.tampering.details.anomalyScore > 0.5 ? 'text-red-400' : ''}>
                                        {(signals.tampering.details.anomalyScore * 100).toFixed(1)}%
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* IP Info */}
                            {signals.ipInfo && (
                              <div className="card">
                                <h4 className="font-medium mb-2 text-blue-400">🌐 IP Intelligence</h4>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {signals.ipInfo.asn && (
                                    <>
                                      <div className="text-gray-400">ASN</div>
                                      <div>{signals.ipInfo.asn.asn} - {signals.ipInfo.asn.name}</div>
                                    </>
                                  )}
                                  {signals.ipInfo.datacenter && (
                                    <>
                                      <div className="text-gray-400">Datacenter</div>
                                      <div className={signals.ipInfo.datacenter.result ? 'text-yellow-400' : 'text-green-400'}>
                                        {signals.ipInfo.datacenter.result ? 'Yes' : 'No'}
                                        {signals.ipInfo.datacenter.name && ` (${signals.ipInfo.datacenter.name})`}
                                      </div>
                                    </>
                                  )}
                                  {signals.ipInfo.geolocation && (
                                    <>
                                      <div className="text-gray-400">Geolocation</div>
                                      <div>
                                        {signals.ipInfo.geolocation.country?.name || '-'}
                                        {signals.ipInfo.geolocation.city?.name && `, ${signals.ipInfo.geolocation.city.name}`}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Velocity */}
                            {signals.velocity && (
                              <div className="card">
                                <h4 className="font-medium mb-2 text-purple-400">⚡ Velocity</h4>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  {signals.velocity.distinctIp && (
                                    <div className="bg-gray-900 p-3 rounded">
                                      <div className="text-gray-400 text-xs mb-1">Distinct IPs</div>
                                      <div className="font-mono">
                                        5m: {signals.velocity.distinctIp['5m']} | 
                                        1h: {signals.velocity.distinctIp['1h']} | 
                                        24h: {signals.velocity.distinctIp['24h']}
                                      </div>
                                    </div>
                                  )}
                                  {signals.velocity.events && (
                                    <div className="bg-gray-900 p-3 rounded">
                                      <div className="text-gray-400 text-xs mb-1">Events</div>
                                      <div className="font-mono">
                                        5m: {signals.velocity.events['5m']} | 
                                        1h: {signals.velocity.events['1h']} | 
                                        24h: {signals.velocity.events['24h']}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {/* Raw JSON (collapsible) */}
                  <details className="card">
                    <summary className="font-semibold cursor-pointer">Raw Smart Signals JSON</summary>
                    <pre className="mt-3 text-xs overflow-auto bg-gray-900 p-3 rounded">
                      {JSON.stringify(parseJsonField(selectedEvent.smart_signals), null, 2)}
                    </pre>
                  </details>
                </div>
              )}

              {/* Labels */}
              <div className="card">
                <h3 className="font-semibold mb-2">Labels</h3>
                {selectedLabels.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {selectedLabels.map((label) => (
                      <div key={label.id} className="flex items-center gap-2 text-sm">
                        <span
                          className={`badge ${
                            label.label === 'GOOD'
                              ? 'badge-allow'
                              : label.label === 'BAD'
                              ? 'badge-block'
                              : 'bg-gray-600'
                          }`}
                        >
                          {label.label}
                        </span>
                        <span className="text-gray-400">by {label.created_by}</span>
                        <span className="text-gray-400">
                          {format(new Date(label.created_at), 'PPp')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400 text-sm mb-4">No labels yet</div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddLabel('GOOD')}
                    className="btn btn-success text-sm"
                  >
                    Mark Good
                  </button>
                  <button
                    onClick={() => handleAddLabel('BAD')}
                    className="btn btn-danger text-sm"
                  >
                    Mark Bad
                  </button>
                  <button
                    onClick={() => handleAddLabel('FALSE_POSITIVE')}
                    className="btn btn-secondary text-sm"
                  >
                    False Positive
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
